// GrowthOS ecommerce starter guidance.
//
// These follow the exact contract of the original guides in src/pages/Playbooks.jsx:
//   id         unique, kebab-case, deep-linkable via ?guide=<id>
//   icon       a key of ICONS in Playbooks.jsx (must exist there or the modal falls back)
//   color      accent hex, kept consistent per category
//   category   searchable grouping (not a filter control)
//   assetTypes drives the asset-type filter, so the vocabulary stays deliberately small
//   outcomes   MUST be values from OUTCOMES; anything else breaks the outcome filter
//   rank       "Recommended" order; the original eight hold ranks 1-8
//   labels     three short chips shown on the card
//   helps      one sentence describing how to FRAME the request (feeds the Slack prompt)
//   before     context the requester supplies; trailing entries marked optional
//   outline    four steps, ALWAYS ending on an explicit approval / no-publish boundary
//   tip        one practical note
//
// The approval boundary in `outline` is not decoration. This dashboard produces
// reviewable drafts and never executes, so every guide must end without an
// implied publish, send, schedule, or purchase action.
//
// Deliberately NOT included, because an equivalent guide already exists and
// duplicate cards would split the same request into two entries:
//   "Review a Landing Page"            -> landing-review
//   "Improve a Product Page"           -> product-page
//   "Plan a Product Launch"            -> product-launch
//   "Build a Campaign Landing Page"    -> campaign-page
//   "Create a Lifecycle Flow"          -> lifecycle-flow
//   "Create a Social Content Calendar" -> social-batch
//   "Draft an Email Campaign"          -> message-campaign (covers email and SMS)

export const STARTER_GUIDES = [
  // ---------------------------------------------------------------- Marketing
  {
    id: "promotion-calendar",
    icon: "CalendarDays",
    color: "#4690D9",
    category: "Campaign",
    assetTypes: ["Campaign Plan"],
    outcomes: ["Revenue", "Average Order Value"],
    rank: 9,
    title: "Build a Promotion Calendar",
    labels: ["Calendar", "Margin", "Capacity"],
    helps: "Frame a request for a monthly promotion plan built around product priorities, margin limits, inventory cover, holidays, customer segments, and realistic channel capacity.",
    before: [
      { item: "Months or quarter to cover" },
      { item: "Products or collections to prioritise" },
      { item: "Margin or discount limits" },
      { item: "Inventory constraints and key dates", optional: true },
    ],
    outline: [
      "Confirm the period, priority products, margin floor, and channel capacity.",
      "Lay out the promotions by week with offer depth, audience, and channel mix.",
      "Flag conflicts across inventory cover, overlapping offers, and customer fatigue.",
      "Return the calendar for review; do not schedule or launch any promotion without approval.",
    ],
    tip: "State the margin floor up front. It is the constraint that decides which promotions are actually viable.",
  },
  {
    id: "sms-campaign",
    icon: "Smartphone",
    color: "#4690D9",
    category: "Lifecycle",
    assetTypes: ["Email/SMS Campaign"],
    outcomes: ["Revenue", "Lifetime Value"],
    rank: 10,
    title: "Plan an SMS Campaign",
    labels: ["Copy", "Compliance", "Timing"],
    helps: "Frame a request for concise SMS or MMS copy with clear offer logic, compliance-safe language, send timing, and explicit audience rules.",
    before: [
      { item: "Offer or announcement" },
      { item: "Audience rules and exclusions" },
      { item: "Preferred send window and time zone" },
      { item: "Any claims or wording to avoid", optional: true },
    ],
    outline: [
      "Confirm the offer, audience rules, exclusions, and send window.",
      "Draft message variants within SMS length limits, including the opt-out convention.",
      "Note compliance-sensitive wording and any claims that need review.",
      "Return the drafts for review; do not schedule or send any message without approval.",
    ],
    tip: "Quiet hours and time zones matter more on SMS than on email. Give the send window explicitly.",
  },
  {
    id: "email-performance-audit",
    icon: "LineChart",
    color: "#4690D9",
    category: "Lifecycle",
    assetTypes: ["Analysis"],
    outcomes: ["Revenue", "Lifetime Value"],
    rank: 11,
    title: "Audit Email Performance",
    labels: ["Evidence", "Deliverability", "Next Send"],
    helps: "Frame a review of campaign revenue, click rate, conversion rate, unsubscribe rate, and deliverability that ends in a recommendation for the next send.",
    before: [
      { item: "Time period to review" },
      { item: "Campaigns or flows in scope" },
      { item: "The metric you are most worried about", optional: true },
    ],
    outline: [
      "Use only available, workspace-approved sending evidence for the period.",
      "Report revenue, click rate, conversion rate, unsubscribe rate, and deliverability signals.",
      "Separate what the data shows from what it suggests, and name any missing or stale source.",
      "End with prioritised recommendations for the next send; do not change any flow or schedule without approval.",
    ],
    tip: "Ask for unsubscribe and deliverability alongside revenue. A strong revenue week can still be damaging the list.",
  },
  {
    id: "audience-segment",
    icon: "Users",
    color: "#4690D9",
    category: "Lifecycle",
    assetTypes: ["Audience Segment"],
    outcomes: ["Lifetime Value", "Revenue"],
    rank: 12,
    title: "Build an Audience Segment",
    labels: ["Definition", "Rules", "Review"],
    helps: "Frame a request to define a high-value customer group by purchase behaviour, engagement, product interest, subscription status, or lifecycle stage.",
    before: [
      { item: "What the segment is for" },
      { item: "Behaviour or traits that qualify someone" },
      { item: "Time window to consider" },
      { item: "Customers to exclude", optional: true },
    ],
    outline: [
      "Confirm the purpose of the segment and how it will be used.",
      "Express the qualifying rules precisely, including time windows and exclusions.",
      "Estimate the size and note any rule that cannot be evaluated from available data.",
      "Return the definition for review; do not create or sync the segment without approval.",
    ],
    tip: "Say what the segment is for. The same audience needs different rules for a winback than for a VIP reward.",
  },
  {
    id: "sale-strategy",
    icon: "Percent",
    color: "#4690D9",
    category: "Campaign",
    assetTypes: ["Campaign Plan"],
    outcomes: ["Revenue", "Average Order Value"],
    rank: 13,
    title: "Create a Sale Strategy",
    labels: ["Offer Depth", "Margin", "Fatigue"],
    helps: "Frame a request for a promotional plan that balances revenue, margin, inventory, urgency, offer depth, and customer fatigue.",
    before: [
      { item: "Sale window and occasion" },
      { item: "Revenue or margin goal" },
      { item: "Products and inventory position" },
      { item: "Recent promotions customers have already seen", optional: true },
    ],
    outline: [
      "Confirm the goal, window, inventory position, and recent promotional history.",
      "Compare offer structures against margin, expected volume, and discount depth.",
      "Recommend one structure with the urgency mechanic and audience sequencing.",
      "Return the plan for review; do not publish discounts or codes without approval.",
    ],
    tip: "Share the last few promotions. Repeated depth trains customers to wait for the next discount.",
  },
  {
    id: "newsletter-batch",
    icon: "Send",
    color: "#4690D9",
    category: "Campaign",
    assetTypes: ["Campaign Plan"],
    outcomes: ["Lifetime Value", "Creative"],
    rank: 14,
    title: "Plan a Newsletter Batch",
    labels: ["Themes", "Schedule", "Drafts"],
    helps: "Frame a request that turns blog posts, product education, seasonal hooks, and offers into a scheduled batch of branded newsletters.",
    before: [
      { item: "Number of newsletters and the period" },
      { item: "Source material or topics to draw from" },
      { item: "Balance of education versus offer" },
      { item: "Approved brand examples", optional: true },
    ],
    outline: [
      "Confirm the batch size, period, and the education-to-offer balance.",
      "Assign a theme, angle, and primary action to each newsletter.",
      "Draft subject options and body copy in the approved brand voice.",
      "Return the batch as a reviewable schedule; do not send or schedule without approval.",
    ],
    tip: "Ask for one coherent batch rather than separate one-off sends, so the sequence builds instead of repeating.",
  },
  {
    id: "product-spotlight-copy",
    icon: "FileText",
    color: "#4690D9",
    category: "Campaign",
    assetTypes: ["Email/SMS Campaign"],
    outcomes: ["Conversion Rate", "Average Order Value"],
    rank: 15,
    title: "Write Product Spotlight Copy",
    labels: ["Problem", "Proof", "CTA"],
    helps: "Frame a request for focused copy that explains one product's problem, benefits, proof, ingredients, offer, and call to action.",
    before: [
      { item: "Product to feature" },
      { item: "Problem it solves for the customer" },
      { item: "Best available proof or reviews" },
      { item: "Offer or incentive", optional: true },
    ],
    outline: [
      "Confirm the product, the customer problem, and the available proof.",
      "Draft copy that leads with the problem and resolves it with benefits and proof.",
      "Keep ingredient, sourcing, or performance claims to what the brand can support.",
      "Return the draft for review; do not publish or send it without approval.",
    ],
    tip: "One product, one problem, one action. Spotlight copy weakens as soon as it tries to sell the whole catalogue.",
  },

  // ------------------------------------------------------- CRO and storefront
  {
    id: "mobile-storefront-audit",
    icon: "Smartphone",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Analysis"],
    outcomes: ["Conversion Rate"],
    rank: 16,
    title: "Audit Mobile Storefront UX",
    labels: ["Mobile", "Friction", "Priorities"],
    helps: "Frame a mobile review covering navigation, page speed, tap targets, checkout friction, image rendering, sticky calls to action, and layout breaks.",
    before: [
      { item: "Pages or journey to review" },
      { item: "Devices or browsers that matter most" },
      { item: "Known problem areas", optional: true },
    ],
    outline: [
      "Walk the named journey as a mobile customer would experience it.",
      "Record friction across navigation, speed, tap targets, rendering, and layout.",
      "Rank the findings by expected conversion impact against implementation effort.",
      "Return a prioritised fix list; do not change the theme or publish edits without approval.",
    ],
    tip: "Name the journey rather than the site. A checkout audit and a discovery audit surface very different problems.",
  },
  {
    id: "pdp-rewrite-brief",
    icon: "FileText",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Product Page"],
    outcomes: ["Conversion Rate", "Average Order Value"],
    rank: 17,
    title: "Create a PDP Rewrite Brief",
    labels: ["Structure", "Claims", "FAQs"],
    helps: "Frame a request that turns product details into a persuasive longform product page structure with sections, claims, proof, FAQs, and offers.",
    before: [
      { item: "Product and current page URL" },
      { item: "Key benefits and differentiators" },
      { item: "Objections customers raise most" },
      { item: "Reviews, testing, or certifications", optional: true },
    ],
    outline: [
      "Confirm the product, its differentiators, and the objections to overcome.",
      "Propose the section order from promise through proof, detail, FAQs, and offer.",
      "Draft the copy for each section and mark any claim needing substantiation.",
      "Return the brief for review; do not publish page changes without approval.",
    ],
    tip: "Flag which claims are substantiated and which are aspirational. That distinction is what keeps a PDP compliant.",
  },
  {
    id: "checkout-friction-review",
    icon: "ShoppingCart",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Analysis"],
    outcomes: ["Conversion Rate", "Revenue"],
    rank: 18,
    title: "Review Checkout Friction",
    labels: ["Cart", "Discounts", "Trust"],
    helps: "Frame a review that identifies problems across cart, checkout, discount logic, subscription options, shipping messaging, and trust signals.",
    before: [
      { item: "Checkout flow and payment methods in use" },
      { item: "Where customers appear to drop off" },
      { item: "Discount or subscription mechanics in play", optional: true },
    ],
    outline: [
      "Walk the full cart-to-confirmation path, including discount and subscription paths.",
      "Identify friction across steps, form fields, messaging, and trust signals.",
      "Separate confirmed problems from suspected ones and note what evidence is missing.",
      "Return a prioritised fix list; do not alter checkout settings without approval.",
    ],
    tip: "Include the discount edge cases. Codes that silently fail at checkout are a common and invisible revenue leak.",
  },
  {
    id: "site-navigation-audit",
    icon: "Compass",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Analysis"],
    outcomes: ["Conversion Rate"],
    rank: 19,
    title: "Audit Site Navigation",
    labels: ["Menus", "Discovery", "Broken Links"],
    helps: "Frame a review of menus, collections, footer links, search behaviour, product discovery paths, and outdated or broken links.",
    before: [
      { item: "Primary customer journeys to support" },
      { item: "Collections or categories that matter most" },
      { item: "Known dead ends or stale links", optional: true },
    ],
    outline: [
      "Map the current navigation and how each priority journey is served.",
      "Identify discovery gaps, redundant paths, mislabelled collections, and broken links.",
      "Recommend a revised structure with the reasoning for each change.",
      "Return the proposal for review; do not edit menus or collections without approval.",
    ],
    tip: "Judge navigation against the journeys you want customers to take, not against how the catalogue is organised internally.",
  },
  {
    id: "storefront-issue-fix",
    icon: "Wrench",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Storefront Fix"],
    outcomes: ["Operations", "Conversion Rate"],
    rank: 20,
    title: "Fix a Storefront Issue",
    labels: ["Diagnosis", "Scope", "Safe Change"],
    helps: "Frame a request to diagnose and document a theme, page, app embed, call to action, modal, styling, or broken-link problem for safe implementation.",
    before: [
      { item: "What is broken and where" },
      { item: "Steps to reproduce it" },
      { item: "Device, browser, and when it started" },
      { item: "Screenshot or recording", optional: true },
    ],
    outline: [
      "Reproduce the issue and confirm the exact conditions that trigger it.",
      "Identify the likely cause and everything the fix would touch.",
      "Document the smallest safe change, with the rollback path stated.",
      "Return the diagnosis and plan; do not apply changes to a live theme without approval.",
    ],
    tip: "Reproduction steps are the whole game. Without them a fix is a guess that may hide the real cause.",
  },
  {
    id: "faq-content",
    icon: "BookOpen",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Support Content"],
    outcomes: ["Conversion Rate", "Operations"],
    rank: 21,
    title: "Create FAQ Content",
    labels: ["Topics", "Confidence", "Support"],
    helps: "Frame a request for customer-facing FAQ sections grouped by topic and written to reduce support load while building purchase confidence.",
    before: [
      { item: "Product or page the FAQs support" },
      { item: "Questions customers actually ask" },
      { item: "Policies that must be stated exactly", optional: true },
    ],
    outline: [
      "Group the real questions into topics that match how customers ask them.",
      "Draft answers that resolve the question without introducing new doubt.",
      "Quote shipping, returns, and guarantee terms exactly as policy states them.",
      "Return the drafts for review; do not publish FAQ content without approval.",
    ],
    tip: "Source the questions from real support tickets. Invented FAQs answer things nobody was asking.",
  },
  {
    id: "conversion-tracking-audit",
    icon: "Gauge",
    color: "#16A34A",
    category: "Analytics",
    assetTypes: ["Analysis"],
    outcomes: ["Operations", "Conversion Rate"],
    rank: 22,
    title: "Audit Conversion Tracking",
    labels: ["Events", "UTMs", "Trust"],
    helps: "Frame a review of whether key events, pixels, UTMs, analytics, and platform attribution are set up clearly enough to trust the numbers.",
    before: [
      { item: "Platforms and pixels in use" },
      { item: "Events that must be tracked" },
      { item: "Numbers that currently look wrong", optional: true },
    ],
    outline: [
      "Inventory the tracked events, pixels, and UTM conventions actually in place.",
      "Identify gaps, duplicates, and inconsistencies that would distort reporting.",
      "State plainly which numbers can be trusted today and which cannot.",
      "Return the findings and fix list; do not change tracking configuration without approval.",
    ],
    tip: "Do this before any spend decision. Every optimisation downstream inherits whatever this audit finds.",
  },

  // ------------------------------------------------------------- Paid media
  {
    id: "meta-ads-review",
    icon: "BarChart3",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Analysis"],
    outcomes: ["Cost Per New Customer", "Revenue"],
    rank: 23,
    title: "Review Meta Ads Performance",
    labels: ["Evidence", "Fatigue", "Next Actions"],
    helps: "Frame a review of campaigns, ad sets, creatives, hooks, spend, cost per acquisition, return on ad spend, fatigue, and audience quality.",
    before: [
      { item: "Time period to review" },
      { item: "Campaigns or ad sets in scope" },
      { item: "Target cost per acquisition or return on ad spend" },
      { item: "Recent changes to spend or creative", optional: true },
    ],
    outline: [
      "Use only available, workspace-approved ad platform evidence for the period.",
      "Report performance by campaign, ad set, and creative against the stated target.",
      "Distinguish creative fatigue from audience saturation and from tracking gaps.",
      "End with prioritised next actions; do not change budgets, targeting, or creative without approval.",
    ],
    tip: "Give the target cost per acquisition. Without it, performance can only be described, not judged.",
  },
  {
    id: "meta-ads-campaign",
    icon: "Megaphone",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Campaign Plan"],
    outcomes: ["Cost Per New Customer", "Revenue"],
    rank: 24,
    title: "Plan a Meta Ads Campaign",
    labels: ["Structure", "Angles", "Launch Checklist"],
    helps: "Frame a request that defines campaign structure, offer, audiences, creative angles, naming, budgets, exclusions, and a launch checklist.",
    before: [
      { item: "Objective and offer" },
      { item: "Budget and campaign window" },
      { item: "Audiences to target and exclude" },
      { item: "Existing creative or angles that worked", optional: true },
    ],
    outline: [
      "Confirm the objective, offer, budget, and audience boundaries.",
      "Propose the campaign and ad set structure with a consistent naming convention.",
      "Define creative angles per audience and the testing plan across them.",
      "Return the plan and launch checklist; do not create or publish campaigns without approval.",
    ],
    tip: "Set the naming convention before launch. Renaming later breaks the reporting history you will want to read.",
  },
  {
    id: "static-ad-concepts",
    icon: "Image",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Ad Creative"],
    outcomes: ["Creative", "Cost Per New Customer"],
    rank: 25,
    title: "Generate Static Ad Concepts",
    labels: ["Angles", "Layouts", "Briefs"],
    helps: "Frame a request for ad angles, hooks, layouts, copy, product callouts, and creative briefs for Meta or paid social.",
    before: [
      { item: "Product and offer to feature" },
      { item: "Audience and the angle to test" },
      { item: "Number of concepts and placements" },
      { item: "Brand guidelines or approved assets", optional: true },
    ],
    outline: [
      "Confirm the product, audience, angle, and required placements.",
      "Draft distinct concepts, each with hook, layout direction, and on-image copy.",
      "Note the product callouts and any claim that needs substantiation.",
      "Return the concepts as briefs for review; do not produce final assets or launch ads without approval.",
    ],
    tip: "Ask for genuinely different angles, not variations of one idea. Near-identical concepts cannot teach you anything.",
  },
  {
    id: "google-ads-audit",
    icon: "Search",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Analysis"],
    outcomes: ["Cost Per New Customer", "Revenue"],
    rank: 26,
    title: "Audit Google Ads Spend",
    labels: ["Pacing", "Waste", "Reallocation"],
    helps: "Frame a review of budget pacing, campaign structure, search terms, return on ad spend, wasted spend, product feed issues, and reallocation opportunities.",
    before: [
      { item: "Time period and account scope" },
      { item: "Monthly budget and pacing expectation" },
      { item: "Target return on ad spend" },
      { item: "Products that must stay funded", optional: true },
    ],
    outline: [
      "Review pacing and structure against the stated budget and target.",
      "Identify wasted spend across search terms, match types, and feed problems.",
      "Recommend reallocation with the expected effect of each move.",
      "Return the findings; do not change budgets, bids, or campaign settings without approval.",
    ],
    tip: "Name the products that must stay funded, or a pure efficiency read may recommend starving a strategic line.",
  },
  {
    id: "brand-capture-plan",
    icon: "ShieldCheck",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Campaign Plan"],
    outcomes: ["Cost Per New Customer", "Revenue"],
    rank: 27,
    title: "Build a Search Brand Capture Plan",
    labels: ["Branded Search", "Competitors", "Efficiency"],
    helps: "Frame a request for branded search campaigns that protect existing demand, control competitor leakage, and keep spend efficient.",
    before: [
      { item: "Brand terms and common misspellings" },
      { item: "Competitors bidding on your brand", optional: true },
      { item: "Current branded spend and performance", optional: true },
    ],
    outline: [
      "Confirm the brand term set, including misspellings and product-name variants.",
      "Assess where demand is leaking to competitors or being paid for unnecessarily.",
      "Propose the campaign structure, match types, negatives, and a spend ceiling.",
      "Return the plan; do not launch campaigns or change bids without approval.",
    ],
    tip: "Branded search can quietly bill you for traffic that was already yours. Ask for the incremental case, not just the return figure.",
  },
  {
    id: "pmax-search-terms",
    icon: "Filter",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Analysis"],
    outcomes: ["Cost Per New Customer"],
    rank: 28,
    title: "Review PMax Search Terms",
    labels: ["Query Themes", "Exclusions", "Cleanup"],
    helps: "Frame a review that identifies poor-fit query themes, exclusions, product-feed issues, and campaign cleanup opportunities.",
    before: [
      { item: "Campaigns and time period" },
      { item: "What a good-fit query looks like" },
      { item: "Existing exclusion lists", optional: true },
    ],
    outline: [
      "Group the search terms into themes rather than reviewing them line by line.",
      "Identify poor-fit themes and the spend attached to each.",
      "Recommend exclusions and feed corrections, noting the risk of over-excluding.",
      "Return the recommendations; do not apply exclusions or feed edits without approval.",
    ],
    tip: "Define a good-fit query first. Otherwise the review just reflects assumptions about intent.",
  },
  {
    id: "ad-disapprovals",
    icon: "ShieldAlert",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Analysis"],
    outcomes: ["Operations"],
    rank: 29,
    title: "Diagnose Ad Disapprovals",
    labels: ["Policy", "Claims", "Resubmission"],
    helps: "Frame an investigation into policy flags, landing page issues, claims risk, account warnings, and the steps needed to resubmit safely.",
    before: [
      { item: "Platform and the disapproval message" },
      { item: "Affected ads and landing pages" },
      { item: "Prior warnings on the account", optional: true },
    ],
    outline: [
      "Record the exact policy cited and every asset it affects.",
      "Identify the likely trigger across ad copy, creative, and landing page claims.",
      "Specify the changes needed and the order to resubmit them in.",
      "Return the plan; do not edit live ads or appeal without approval.",
    ],
    tip: "Fix the landing page as well as the ad. Repeated appeals on an unchanged page put the whole account at risk.",
  },
  {
    id: "true-roas",
    icon: "Receipt",
    color: "#D97706",
    category: "Paid Media",
    assetTypes: ["Analysis"],
    outcomes: ["Revenue", "Cost Per New Customer"],
    rank: 30,
    title: "Reconcile True ROAS",
    labels: ["Attribution", "Order Data", "Profitability"],
    helps: "Frame a comparison of ad platform revenue against store and email platform order data to understand real profitability.",
    before: [
      { item: "Time period to reconcile" },
      { item: "Platforms reporting the revenue" },
      { item: "Attribution windows in use" },
      { item: "Margin or cost of goods", optional: true },
    ],
    outline: [
      "Pull each platform's reported revenue for an identical period and window.",
      "Quantify the gaps and explain the double counting or attribution behind them.",
      "State a defensible view of real profitability and its remaining uncertainty.",
      "Return the reconciliation; do not restate reporting or reallocate spend without approval.",
    ],
    tip: "Ask which single source will be treated as truth. Without that decision, reconciliation repeats every month.",
  },

  // ---------------------------------------------------- Creative and content
  {
    id: "creative-batch",
    icon: "Layers",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Creative Brief"],
    outcomes: ["Creative"],
    rank: 31,
    title: "Plan a Creative Batch",
    labels: ["Angles", "Formats", "Review"],
    helps: "Frame a request for a batch of ad, email, landing page, and social creative organised by angle, audience, product, and format.",
    before: [
      { item: "Channels and formats needed" },
      { item: "Products or offers to feature" },
      { item: "Number of concepts per angle" },
      { item: "Approved assets to build from", optional: true },
    ],
    outline: [
      "Confirm the channels, formats, products, and volume required.",
      "Group the batch by angle so each concept tests something distinct.",
      "Specify per-asset requirements including dimensions, copy, and product accuracy.",
      "Return the batch as briefs for review; do not produce or publish final assets without approval.",
    ],
    tip: "Organise by angle rather than by channel. One strong angle usually resizes across placements.",
  },
  {
    id: "ad-hooks",
    icon: "Lightbulb",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Ad Creative"],
    outcomes: ["Creative", "Cost Per New Customer"],
    rank: 32,
    title: "Write Ad Hooks",
    labels: ["Hooks", "Variety", "Testing"],
    helps: "Frame a request for problem-aware, benefit-driven, testimonial, offer, ingredient, comparison, and founder-story hooks built for testing.",
    before: [
      { item: "Product and audience" },
      { item: "Problem the product solves" },
      { item: "Number of hooks per style" },
      { item: "Testimonials or proof to draw from", optional: true },
    ],
    outline: [
      "Confirm the product, audience, and the core problem being addressed.",
      "Write hooks grouped by style so each group tests a different motivation.",
      "Keep every claim within what the brand can substantiate.",
      "Return the hooks for review; do not launch them as ads without approval.",
    ],
    tip: "Ask for hooks grouped by style. Testing ten variations of one appeal only tells you about that appeal.",
  },
  {
    id: "blog-to-campaign",
    icon: "Repeat",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Creative Brief"],
    outcomes: ["Creative", "Lifetime Value"],
    rank: 33,
    title: "Turn a Blog Into Campaign Assets",
    labels: ["Repurpose", "Multi-channel", "Drafts"],
    helps: "Frame a request to repurpose an educational article into email copy, SMS, ads, landing page sections, FAQs, and social posts.",
    before: [
      { item: "Article URL or content" },
      { item: "Channels to repurpose into" },
      { item: "Product or offer to connect it to" },
      { item: "Audience the article suits best", optional: true },
    ],
    outline: [
      "Identify the article's core insight and the proof supporting it.",
      "Adapt that insight per channel rather than trimming the same text down.",
      "Connect each asset to the product without losing the educational value.",
      "Return the drafts for review; do not publish or send any asset without approval.",
    ],
    tip: "Repurposing works when the insight is adapted per channel. Truncating an article rarely reads well anywhere.",
  },
  {
    id: "image-brief",
    icon: "Image",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Creative Brief"],
    outcomes: ["Creative"],
    rank: 34,
    title: "Build an Image Brief",
    labels: ["Direction", "Specs", "Accuracy"],
    helps: "Frame a request for clear generation or design instructions covering product imagery, email graphics, ads, blog heroes, and page visuals.",
    before: [
      { item: "Where the image will be used" },
      { item: "Product or subject and required accuracy" },
      { item: "Dimensions and format" },
      { item: "Reference images or brand guidelines", optional: true },
    ],
    outline: [
      "Confirm placement, dimensions, format, and how the subject must be represented.",
      "Describe composition, lighting, styling, and mood in specific terms.",
      "State what must be accurate and what may be interpreted.",
      "Return the brief for review; do not treat generated drafts as approved brand assets.",
    ],
    tip: "Be explicit about product accuracy. Packaging, colour, and labels are exactly what generated imagery gets wrong.",
  },
  {
    id: "creative-qa",
    icon: "ClipboardCheck",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Checklist"],
    outcomes: ["Creative", "Operations"],
    rank: 35,
    title: "QA Creative Assets",
    labels: ["Specs", "Claims", "Readiness"],
    helps: "Frame a check of dimensions, brand fit, text legibility, claims risk, product accuracy, mobile rendering, and channel readiness before anything ships.",
    before: [
      { item: "Assets to check and their destination channels" },
      { item: "Brand guidelines to check against" },
      { item: "Claims that need substantiation", optional: true },
    ],
    outline: [
      "Check each asset against its channel's specifications and safe areas.",
      "Review brand fit, legibility at real display size, and product accuracy.",
      "Flag claims risk and anything requiring legal or compliance review.",
      "Return a pass or fail per asset with required fixes; do not publish without approval.",
    ],
    tip: "Check legibility at actual display size. Text that reads fine on a desktop preview often fails in a mobile feed.",
  },
  {
    id: "product-education-brief",
    icon: "BookOpen",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Creative Brief"],
    outcomes: ["Lifetime Value", "Creative"],
    rank: 36,
    title: "Create a Product Education Brief",
    labels: ["Mechanism", "Objections", "Proof"],
    helps: "Frame a request that explains ingredients, mechanisms, objections, use cases, and proof points in customer-friendly language.",
    before: [
      { item: "Product and what it contains or how it works" },
      { item: "Customer questions and objections" },
      { item: "Proof, testing, or certifications available" },
      { item: "Reading level or tone to match", optional: true },
    ],
    outline: [
      "Confirm the mechanism and the level of detail the audience needs.",
      "Explain how the product works in plain language, without overstating it.",
      "Address objections and use cases with the proof that supports each.",
      "Return the brief for review; do not publish education content without approval.",
    ],
    tip: "Ask for plain language over technical accuracy alone. A mechanism customers cannot follow does not persuade them.",
  },

  // --------------------------------------------------- Support and retention
  {
    id: "support-triage",
    icon: "Headphones",
    color: "#EC4899",
    category: "Retention",
    assetTypes: ["Analysis"],
    outcomes: ["Operations", "Lifetime Value"],
    rank: 37,
    title: "Triage Customer Support Emails",
    labels: ["Classify", "Urgency", "Drafts"],
    helps: "Frame a request to classify support messages, suggest replies, identify urgent issues, and flag refund, subscription, shipping, or product concerns.",
    before: [
      { item: "Messages or time period to triage" },
      { item: "Categories and what counts as urgent" },
      { item: "Policies for refunds, returns, and cancellations", optional: true },
    ],
    outline: [
      "Classify each message by category and urgency using the stated rules.",
      "Surface anything time-critical, at risk of escalation, or affecting many customers.",
      "Draft suggested replies grounded in stated policy, not assumption.",
      "Return the triage and drafts for review; do not send any reply to a customer without approval.",
    ],
    tip: "Define urgent explicitly. Without a rule, urgency gets judged by tone rather than by actual business impact.",
  },
  {
    id: "support-macros",
    icon: "MessageSquare",
    color: "#EC4899",
    category: "Retention",
    assetTypes: ["Support Content"],
    outcomes: ["Operations"],
    rank: 38,
    title: "Build Support Macros",
    labels: ["Reusable", "Policy", "Tone"],
    helps: "Frame a request for reusable responses covering order status, returns, subscriptions, product questions, damaged shipments, and discount issues.",
    before: [
      { item: "Scenarios to cover" },
      { item: "Policies that must be stated exactly" },
      { item: "Tone and signature conventions" },
      { item: "Existing replies that work well", optional: true },
    ],
    outline: [
      "Confirm the scenarios and the policy language required for each.",
      "Draft one macro per scenario with clear placeholders for specifics.",
      "Keep the tone consistent and note where an agent must add judgement.",
      "Return the macros for review; do not load them into the support tool without approval.",
    ],
    tip: "Mark where a human must personalise. Macros that read as fully automated damage the relationships they touch.",
  },
  {
    id: "support-themes-audit",
    icon: "PieChart",
    color: "#EC4899",
    category: "Retention",
    assetTypes: ["Analysis"],
    outcomes: ["Operations", "Conversion Rate"],
    rank: 39,
    title: "Audit Support Themes",
    labels: ["Patterns", "Root Cause", "Site Gaps"],
    helps: "Frame an analysis of recurring tickets to find product confusion, checkout issues, shipping problems, subscription friction, or missing site content.",
    before: [
      { item: "Time period and ticket volume" },
      { item: "How tickets are currently categorised", optional: true },
      { item: "Changes shipped during the period", optional: true },
    ],
    outline: [
      "Group tickets into themes by underlying cause, not by surface wording.",
      "Quantify each theme and identify what in the product or site creates it.",
      "Separate problems fixable with content from those needing an operational change.",
      "Return the findings and recommendations; do not change site content or policy without approval.",
    ],
    tip: "The best outcome here is a site or product fix that removes the ticket, not a faster way to answer it.",
  },
  {
    id: "post-purchase-education",
    icon: "Truck",
    color: "#EC4899",
    category: "Retention",
    assetTypes: ["Email/SMS Flow"],
    outcomes: ["Lifetime Value"],
    rank: 40,
    title: "Create Post-Purchase Education",
    labels: ["Onboarding", "Expectations", "Repeat"],
    helps: "Frame a request for follow-up messages that teach customers how to use the product, set expectations, reduce refunds, and encourage repeat purchase.",
    before: [
      { item: "Product and how it should be used" },
      { item: "Common misuse or disappointment reasons" },
      { item: "Shipping and delivery expectations" },
      { item: "Replenishment or subscription timing", optional: true },
    ],
    outline: [
      "Map the first weeks of ownership and what the customer needs at each point.",
      "Draft messages that set expectations early and teach correct use.",
      "Address the reasons customers request refunds before they arise.",
      "Return the flow for review; do not activate or send any message without approval.",
    ],
    tip: "Front-load expectation setting. Most refund requests trace back to a mismatch formed in the first few days.",
  },
  {
    id: "winback-campaign",
    icon: "RefreshCw",
    color: "#EC4899",
    category: "Retention",
    assetTypes: ["Campaign Plan"],
    outcomes: ["Lifetime Value", "Revenue"],
    rank: 41,
    title: "Plan a Winback Campaign",
    labels: ["Lapsed", "Offer", "Timing"],
    helps: "Frame a request for audience rules, offers, copy, timing, and product recommendations aimed at lapsed customers.",
    before: [
      { item: "What counts as lapsed for this brand" },
      { item: "Offer you are willing to make" },
      { item: "Products to recommend" },
      { item: "Why customers typically lapse", optional: true },
    ],
    outline: [
      "Define the lapsed window precisely and the exclusions that apply.",
      "Sequence the messages so the incentive escalates rather than opening at maximum depth.",
      "Recommend products based on prior purchase behaviour.",
      "Return the plan and drafts for review; do not send or schedule without approval.",
    ],
    tip: "Do not lead with the deepest discount. Escalating offers recover margin from those who would have returned anyway.",
  },
  {
    id: "subscription-retention",
    icon: "Repeat",
    color: "#EC4899",
    category: "Retention",
    assetTypes: ["Analysis"],
    outcomes: ["Lifetime Value", "Revenue"],
    rank: 42,
    title: "Improve Subscription Retention",
    labels: ["Churn Reasons", "Save Offers", "Timing"],
    helps: "Frame a review of cancellation reasons, subscription messaging, replenishment timing, save offers, and customer education.",
    before: [
      { item: "Cancellation reasons currently collected" },
      { item: "Subscription cadence and products" },
      { item: "Save offers available today", optional: true },
    ],
    outline: [
      "Group cancellation reasons by cause and quantify each.",
      "Assess whether cadence, messaging, or education is driving the churn.",
      "Recommend interventions matched to each reason rather than one blanket save offer.",
      "Return the recommendations; do not change subscription settings or offers without approval.",
    ],
    tip: "Match the intervention to the reason. Discounting someone who cancelled for over-supply makes the problem worse.",
  },

  // ---------------------------------------- Operations and merchandising
  {
    id: "supplier-po-map",
    icon: "Handshake",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Operations Plan"],
    outcomes: ["Operations"],
    rank: 43,
    title: "Create a Supplier/PO Setup Map",
    labels: ["Vendors", "SKUs", "Procurement"],
    helps: "Frame a request to normalise vendor, component, SKU, cost, replenishment, and procurement data so purchasing can operate cleanly.",
    before: [
      { item: "Suppliers and what each provides" },
      { item: "SKU and component structure" },
      { item: "Costs and lead times" },
      { item: "Current purchase order process", optional: true },
    ],
    outline: [
      "Inventory suppliers, components, SKUs, costs, and lead times as they exist today.",
      "Normalise naming and structure, listing every inconsistency found.",
      "Define the purchase order fields and approval steps purchasing needs.",
      "Return the map for review; do not place orders or change supplier records without approval.",
    ],
    tip: "Expect the inconsistencies to be the real output. Naming conflicts are what break procurement automation later.",
  },
  {
    id: "inventory-risk-audit",
    icon: "Boxes",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Analysis"],
    outcomes: ["Operations", "Revenue"],
    rank: 44,
    title: "Audit Inventory Risk",
    labels: ["Stockout", "Overstock", "Dependencies"],
    helps: "Frame a review that identifies products at risk of stockout, overstock, launch conflict, promotional risk, or supplier dependency.",
    before: [
      { item: "Products or collections in scope" },
      { item: "Current stock and inbound quantities" },
      { item: "Upcoming promotions or launches" },
      { item: "Supplier lead times", optional: true },
    ],
    outline: [
      "Compare stock cover against expected demand, including planned promotions.",
      "Flag stockout and overstock risk with the date each becomes critical.",
      "Identify single-supplier dependencies and launch conflicts.",
      "Return the risk list and recommended actions; do not place or change orders without approval.",
    ],
    tip: "Include planned promotions. A promotion on a thin SKU converts a healthy stock position into a stockout.",
  },
  {
    id: "product-bundles",
    icon: "Package",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Merchandising Plan"],
    outcomes: ["Average Order Value", "Revenue"],
    rank: 45,
    title: "Build Product Bundles",
    labels: ["Compatibility", "Margin", "AOV"],
    helps: "Frame a request for bundle recommendations based on customer goals, average order value targets, product compatibility, margin, and merchandising strategy.",
    before: [
      { item: "Products available to bundle" },
      { item: "Average order value target" },
      { item: "Margin limits and costs" },
      { item: "Products frequently bought together", optional: true },
    ],
    outline: [
      "Confirm the target, margin limits, and which products may be combined.",
      "Propose bundles built around a customer goal rather than inventory convenience.",
      "Model the pricing against margin and the average order value target.",
      "Return the recommendations; do not create bundle products or pricing without approval.",
    ],
    tip: "Build around a customer goal. Bundles assembled to move slow stock rarely convert.",
  },
  {
    id: "collection-merchandising",
    icon: "LayoutGrid",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Merchandising Plan"],
    outcomes: ["Conversion Rate", "Average Order Value"],
    rank: 46,
    title: "Review Collection Merchandising",
    labels: ["Ordering", "Filters", "Cross-sell"],
    helps: "Frame a review of product ordering, collection naming, filters, cross-sells, and offer visibility.",
    before: [
      { item: "Collections to review" },
      { item: "Products to prioritise and why" },
      { item: "Current sort logic and filters", optional: true },
    ],
    outline: [
      "Review how each collection currently orders, names, and filters its products.",
      "Identify where priority products are buried or discovery breaks down.",
      "Recommend ordering, naming, filter, and cross-sell changes with reasoning.",
      "Return the recommendations; do not reorder collections or publish changes without approval.",
    ],
    tip: "Say why a product is a priority. Margin, inventory, and strategy each imply a different sort order.",
  },
  {
    id: "product-data-cleanup",
    icon: "Tag",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Operations Plan"],
    outcomes: ["Operations"],
    rank: 47,
    title: "Clean Up Product Data",
    labels: ["Standards", "Metafields", "SEO"],
    helps: "Frame a request to standardise product titles, descriptions, SKUs, metafields, costs, tags, variants, images, and SEO fields.",
    before: [
      { item: "Products or collections in scope" },
      { item: "Naming and SKU conventions to apply" },
      { item: "Fields that must be populated" },
      { item: "Known bad or missing data", optional: true },
    ],
    outline: [
      "Audit the named products against the stated conventions and required fields.",
      "List every inconsistency, gap, and duplicate found.",
      "Propose the corrected values, flagging anything needing a business decision.",
      "Return the changes for review; do not write to product records without approval.",
    ],
    tip: "Agree the convention before the cleanup. Standardising to the wrong pattern costs twice to undo.",
  },
  {
    id: "replenishment-logic",
    icon: "Workflow",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Operations Plan"],
    outcomes: ["Operations", "Lifetime Value"],
    rank: 48,
    title: "Map Replenishment Logic",
    labels: ["Reorder", "Lead Times", "Rules"],
    helps: "Frame a request to define reorder timing, component dependencies, supplier lead times, subscription demand, and procurement rules.",
    before: [
      { item: "Products and their components" },
      { item: "Supplier lead times and minimum order quantities" },
      { item: "Subscription demand and cadence" },
      { item: "Safety stock preferences", optional: true },
    ],
    outline: [
      "Document current demand, lead times, and component dependencies.",
      "Define reorder points and quantities, stating the assumptions behind each.",
      "Account for subscription demand separately from one-off purchases.",
      "Return the rules for review; do not place orders or automate purchasing without approval.",
    ],
    tip: "Keep subscription demand separate. It is far more predictable, and blending it hides the volatility in the rest.",
  },
  {
    id: "launch-dependency-checklist",
    icon: "ListChecks",
    color: "#0891B2",
    category: "Operations",
    assetTypes: ["Checklist"],
    outcomes: ["Operations", "Revenue"],
    rank: 49,
    title: "Create a Launch Dependency Checklist",
    labels: ["Dependencies", "Owners", "Readiness"],
    helps: "Frame a request to track product page, inventory, discounts, email, SMS, ads, creative, quality assurance, support, and analytics readiness before launch.",
    before: [
      { item: "What is launching and when" },
      { item: "Channels involved" },
      { item: "Owners for each area", optional: true },
    ],
    outline: [
      "List every dependency across store, marketing, creative, support, and analytics.",
      "Order them by the date each must be complete, working back from launch.",
      "Mark which items block launch and which can trail it.",
      "Return the checklist for review; do not action any item or publish without approval.",
    ],
    tip: "Separate true blockers from nice-to-haves. Everything looks critical the week before a launch.",
  },

  // ------------------------------------------------- Analytics and strategy
  {
    id: "weekly-growth-report",
    icon: "Presentation",
    color: "#0F766E",
    category: "Analytics",
    assetTypes: ["Report"],
    outcomes: ["Revenue", "Operations"],
    rank: 50,
    title: "Create a Weekly Growth Report",
    labels: ["Metrics", "Movement", "Actions"],
    helps: "Frame a request for a weekly summary of revenue, spend, return on ad spend, marketing efficiency ratio, average order value, conversion rate, message revenue, subscriptions, and key actions.",
    before: [
      { item: "Week or period to report" },
      { item: "Metrics that must appear" },
      { item: "Comparison basis, such as prior week or year" },
      { item: "Audience for the report", optional: true },
    ],
    outline: [
      "Use only available, workspace-approved sources for the period.",
      "Report each metric with its comparison and state the freshness of every source.",
      "Explain material movements, separating fact from interpretation.",
      "End with prioritised actions; name any missing source rather than substituting a value.",
    ],
    tip: "Insist that stale or missing sources are named. A report that silently fills gaps is worse than one with holes.",
  },
  {
    id: "revenue-movement",
    icon: "LineChart",
    color: "#0F766E",
    category: "Analytics",
    assetTypes: ["Analysis"],
    outcomes: ["Revenue"],
    rank: 51,
    title: "Diagnose Revenue Movement",
    labels: ["Drivers", "Channels", "Evidence"],
    helps: "Frame an explanation of why revenue changed across channel, campaign, product, conversion rate, traffic, average order value, repeat purchase, or stock status.",
    before: [
      { item: "Periods to compare" },
      { item: "Size and direction of the change" },
      { item: "Anything you already suspect" },
      { item: "Known changes to spend, stock, or site", optional: true },
    ],
    outline: [
      "Decompose the change into traffic, conversion rate, average order value, and repeat purchase.",
      "Attribute the movement by channel and product where evidence supports it.",
      "State clearly which portion of the change cannot be explained from available data.",
      "End with the most probable drivers ranked by confidence, keeping facts separate from interpretation.",
    ],
    tip: "Require an explicit unexplained remainder. A diagnosis that accounts for exactly 100% is usually overreaching.",
  },
  {
    id: "customer-cohorts",
    icon: "Users",
    color: "#0F766E",
    category: "Analytics",
    assetTypes: ["Analysis"],
    outcomes: ["Lifetime Value", "Revenue"],
    rank: 52,
    title: "Analyze Customer Cohorts",
    labels: ["Repeat", "LTV", "Pathing"],
    helps: "Frame a review of first purchase, repeat purchase, subscription behaviour, lifetime value, product pathing, and cohort quality.",
    before: [
      { item: "Cohort definition, such as first-purchase month" },
      { item: "Time horizon to follow" },
      { item: "Products or channels to compare", optional: true },
    ],
    outline: [
      "Define the cohorts and confirm the horizon each can honestly be measured over.",
      "Report repeat rate, lifetime value, and subscription behaviour per cohort.",
      "Identify which acquisition sources and first products produce better cohorts.",
      "End with recommendations; note where recent cohorts are too young to judge.",
    ],
    tip: "Watch the young cohorts. They almost always look worse simply because they have had less time to repeat.",
  },
  {
    id: "growth-bottlenecks",
    icon: "Target",
    color: "#0F766E",
    category: "Strategy",
    assetTypes: ["Analysis"],
    outcomes: ["Revenue", "Operations"],
    rank: 53,
    title: "Identify Growth Bottlenecks",
    labels: ["Constraint", "Evidence", "Focus"],
    helps: "Frame an assessment of whether the brand is constrained by traffic, conversion, offer, creative, retention, inventory, or execution capacity.",
    before: [
      { item: "Current growth goal" },
      { item: "What you have already tried" },
      { item: "Available performance evidence" },
      { item: "Team or budget limits", optional: true },
    ],
    outline: [
      "Review evidence across acquisition, conversion, retention, inventory, and delivery capacity.",
      "Identify the single binding constraint and the evidence that isolates it.",
      "Explain why the other candidates are not currently the limiting factor.",
      "End with the focused work that would relieve it; do not action anything without approval.",
    ],
    tip: "Push for one binding constraint. A list of seven problems is the same as having no diagnosis.",
  },
  {
    id: "thirty-day-plan",
    icon: "CalendarDays",
    color: "#0F766E",
    category: "Strategy",
    assetTypes: ["Action Plan"],
    outcomes: ["Revenue", "Operations"],
    rank: 54,
    title: "Build a 30-Day Action Plan",
    labels: ["Priorities", "Sequence", "Capacity"],
    helps: "Frame a request to prioritise the highest-leverage work across ads, email, conversion, creative, product, support, and operations for the next month.",
    before: [
      { item: "Primary goal for the month" },
      { item: "Team capacity and who is available" },
      { item: "Work already committed" },
      { item: "Budget available", optional: true },
    ],
    outline: [
      "Confirm the goal, real capacity, and commitments already in flight.",
      "Rank candidate work by expected impact against effort and dependency.",
      "Sequence it across four weeks with owners and success measures.",
      "Return the plan for review; do not begin execution without approval.",
    ],
    tip: "Give honest capacity. A thirty-day plan that assumes an idle team becomes a backlog by week two.",
  },
  {
    id: "offer-economics",
    icon: "CreditCard",
    color: "#0F766E",
    category: "Strategy",
    assetTypes: ["Analysis"],
    outcomes: ["Average Order Value", "Cost Per New Customer"],
    rank: 55,
    title: "Review Offer Economics",
    labels: ["Margin", "Payback", "LTV"],
    helps: "Frame an evaluation of discount depth, gross margin, acquisition cost, average order value, lifetime value, payback period, subscription impact, and profitability.",
    before: [
      { item: "Offer and discount depth" },
      { item: "Cost of goods and gross margin" },
      { item: "Current acquisition cost" },
      { item: "Repeat purchase or subscription rates", optional: true },
    ],
    outline: [
      "Model the unit economics of the offer at the stated margin and acquisition cost.",
      "Calculate the payback period and state every assumption used.",
      "Test how the result changes if repeat purchase underperforms expectations.",
      "End with a clear profitability view; do not change pricing or offers without approval.",
    ],
    tip: "Ask for the downside case. Offers that only work at optimistic repeat rates fail quietly over months.",
  },
  {
    id: "brand-operating-brief",
    icon: "NotebookPen",
    color: "#0F766E",
    category: "Strategy",
    assetTypes: ["Operating Brief"],
    outcomes: ["Operations", "Revenue"],
    rank: 56,
    title: "Create a Brand Operating Brief",
    labels: ["Positioning", "Constraints", "Priorities"],
    helps: "Frame a request to summarise the brand's audience, products, positioning, offers, channels, constraints, goals, and current priorities in one place.",
    before: [
      { item: "Audience and how you position against alternatives" },
      { item: "Products, offers, and channels in use" },
      { item: "Constraints, including margin, capacity, and compliance" },
      { item: "Goals for the next quarter", optional: true },
    ],
    outline: [
      "Capture audience, positioning, products, offers, and active channels.",
      "Record the constraints and decision rules that govern how work happens.",
      "State current priorities and what is explicitly out of scope.",
      "Return the brief for review; treat it as approved context only once confirmed.",
    ],
    tip: "The out-of-scope list matters as much as the priorities. It is what prevents drift on every later request.",
  },
  {
    id: "agent-workstream",
    icon: "Workflow",
    color: "#0F766E",
    category: "Strategy",
    assetTypes: ["Action Plan"],
    outcomes: ["Operations"],
    rank: 57,
    title: "Plan an Agent Workstream",
    labels: ["Tasks", "Approvals", "Metrics"],
    helps: "Frame a request that turns a business objective into agent-ready tasks with approvals, dependencies, owners, deadlines, and success metrics.",
    before: [
      { item: "Business objective" },
      { item: "Deadline and any fixed dates" },
      { item: "Who approves what" },
      { item: "Systems the work will touch", optional: true },
    ],
    outline: [
      "Break the objective into tasks small enough to be reviewed independently.",
      "Map dependencies and mark the approval checkpoint before each irreversible step.",
      "Assign owners, deadlines, and a success measure per task.",
      "Return the workstream for review; do not start execution without approval.",
    ],
    tip: "Put an approval checkpoint before anything irreversible. That boundary is what makes delegation safe.",
  },

  // ----------------------------------------------- Admin and team execution
  {
    id: "project-tasks",
    icon: "ListChecks",
    color: "#64748B",
    category: "Execution",
    assetTypes: ["Task Set"],
    outcomes: ["Operations"],
    rank: 58,
    title: "Create Project Tasks",
    labels: ["Owners", "Dependencies", "Acceptance"],
    helps: "Frame a request that turns a loose ask into clear tasks with owner, due date, notes, dependencies, and acceptance criteria.",
    before: [
      { item: "The request or project to break down" },
      { item: "Who can own the work" },
      { item: "Deadline or milestone" },
      { item: "Definition of done", optional: true },
    ],
    outline: [
      "Restate the request as a concrete outcome before breaking it into tasks.",
      "Write tasks that each have one owner and one verifiable result.",
      "Record dependencies, sequencing, and acceptance criteria per task.",
      "Return the task set for review; do not create tasks in any external tool without approval.",
    ],
    tip: "Insist on acceptance criteria. Tasks without them get marked complete while the outcome is still missing.",
  },
  {
    id: "qa-checklist",
    icon: "ClipboardCheck",
    color: "#64748B",
    category: "Execution",
    assetTypes: ["Checklist"],
    outcomes: ["Operations", "Creative"],
    rank: 59,
    title: "Build a QA Checklist",
    labels: ["Pre-publish", "Links", "Approval"],
    helps: "Frame a request for pre-publish checks covering campaigns, pages, ads, discounts, links, tracking, mobile rendering, and approval status.",
    before: [
      { item: "What is being published and where" },
      { item: "Things that have gone wrong before" },
      { item: "Who signs off before publication", optional: true },
    ],
    outline: [
      "List the checks required for this asset type, ordered as they should be performed.",
      "Include discount codes, link targets, tracking parameters, and mobile rendering.",
      "Mark which checks block publication and who records each sign-off.",
      "Return the checklist for review; do not publish anything on the basis of an unreviewed check.",
    ],
    tip: "Build it from what has actually broken before. Generic checklists miss the mistakes this team really makes.",
  },
  {
    id: "standard-process-doc",
    icon: "BookOpen",
    color: "#64748B",
    category: "Execution",
    assetTypes: ["Process Doc"],
    outcomes: ["Operations"],
    rank: 60,
    title: "Document a Standard Process",
    labels: ["SOP", "Steps", "Handoffs"],
    helps: "Frame a request to convert repeated work into a reusable standard operating procedure for campaigns, launches, support, procurement, reporting, or creative.",
    before: [
      { item: "Process to document" },
      { item: "Who performs each part today" },
      { item: "Tools and access required" },
      { item: "Known failure points", optional: true },
    ],
    outline: [
      "Capture the process as it is actually performed, not as it is imagined.",
      "Write numbered steps with the owner, tool, and expected output for each.",
      "Record handoffs, approval points, and what to do when a step fails.",
      "Return the document for review; do not treat it as standard until approved.",
    ],
    tip: "Document the real process first. Writing the idealised version produces a document nobody follows.",
  },
  {
    id: "tool-connections-review",
    icon: "Link2",
    color: "#64748B",
    category: "Execution",
    assetTypes: ["Analysis"],
    outcomes: ["Operations"],
    rank: 61,
    title: "Review Tool Connections",
    labels: ["Access", "Coverage", "Gaps"],
    helps: "Frame a review of whether the store, messaging, advertising, analytics, support, and project tools are connected and usable for the work planned.",
    before: [
      { item: "Tools that should be connected" },
      { item: "Work that depends on them" },
      { item: "Who administers each tool", optional: true },
    ],
    outline: [
      "List each tool, its connection state, and what the workspace can currently read.",
      "Identify gaps that block the planned work and who must resolve each.",
      "Distinguish a missing connection from missing permissions on an existing one.",
      "Return the findings; do not change credentials or connection settings without approval.",
    ],
    tip: "Connected is not the same as usable. A connection with read-only scope still blocks anything needing writes.",
  },
  {
    id: "client-handoff-doc",
    icon: "Presentation",
    color: "#64748B",
    category: "Execution",
    assetTypes: ["Process Doc"],
    outcomes: ["Operations"],
    rank: 62,
    title: "Prepare a Client Handoff Doc",
    labels: ["Assets", "Open Items", "Next Steps"],
    helps: "Frame a request to package strategy, assets, links, implementation notes, reports, open items, and next steps into a clean handoff.",
    before: [
      { item: "What is being handed over and to whom" },
      { item: "Work completed and where the assets live" },
      { item: "Open items and known risks" },
      { item: "Access the recipient will need", optional: true },
    ],
    outline: [
      "Summarise what was done, why, and the outcome achieved.",
      "Index every asset, link, and report with its location and access requirement.",
      "State open items, risks, and recommended next steps honestly.",
      "Return the document for review; do not share it externally without approval.",
    ],
    tip: "Be candid about open items. A handoff that reads as fully finished creates the next escalation.",
  },
  {
    id: "brand-readiness-audit",
    icon: "ShieldCheck",
    color: "#64748B",
    category: "Execution",
    assetTypes: ["Analysis"],
    outcomes: ["Operations"],
    rank: 63,
    title: "Audit Brand Readiness",
    labels: ["Data", "Permissions", "Decision Rules"],
    helps: "Frame an assessment of whether the brand has the data, tools, assets, permissions, processes, and decision rules needed for agent execution.",
    before: [
      { item: "Work you want the agent to take on" },
      { item: "Tools and data currently connected" },
      { item: "Who approves what today" },
      { item: "Brand guidelines and claim rules", optional: true },
    ],
    outline: [
      "Assess readiness across data, tooling, assets, permissions, process, and decision rules.",
      "Identify what is missing for the intended work and the risk each gap creates.",
      "Rank the gaps by how much they block execution.",
      "Return the assessment and remediation order; do not change access or settings without approval.",
    ],
    tip: "Decision rules are the gap most often missed. Without them, work stalls at every point needing a judgement call.",
  },

  // ------------------------------------------------------- storefront build
  {
    id: "shopify-landing-page",
    icon: "Layers",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Landing Page"],
    outcomes: ["Conversion Rate", "Revenue"],
    // Sits immediately after campaign-page (rank 4) because the natural order is
    // plan the page, then design and build it. A fractional rank avoids
    // renumbering the entire catalogue to insert one card.
    rank: 4.5,
    title: "Design & Build a Landing Page in Shopify",
    labels: ["Sections", "Mobile First", "Preview Link"],
    helps: "Frame a request to design and build a landing page inside Shopify, covering theme sections, the page template, URL handle, mobile layout, and a preview on an unpublished theme.",
    before: [
      { item: "Approved page structure, or the plan to build from" },
      { item: "The offer, audience, and the single action the page drives" },
      { item: "Which theme it lives in, and whether a page template exists" },
      { item: "Brand assets, fonts, and imagery to use", optional: true },
    ],
    outline: [
      "Confirm the approved structure, the single action, and which theme the page belongs to.",
      "Specify the build: which existing sections or blocks to reuse, what must be added, the page template to assign, and the URL handle.",
      "Design it mobile first with the copy, imagery, and proof in place, noting the speed cost of anything new that gets added.",
      "Build on a duplicated, unpublished theme and hand back a preview link; do not publish to the live theme or point traffic at it without approval.",
    ],
    tip: "Ask for existing sections to be reused before any new app is installed. Every extra app is permanent weight on the page.",
  },
];
